import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Loader2, Sparkles, Trash2, Undo2 } from "lucide-react";
import { api, type BackfillJobView, type PlannerPickView } from "../../lib/api.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * THE PLANNER'S PROPOSAL — what goes out, when, and why.
 *
 * Level 1 (Dor, 2026-09-19): the platform proposes, a person decides. Each
 * pick shows its reason and score; approve it, swap it for one of the
 * alternatives it beat, or remove it. Every decision is recorded — it is what
 * the planner learns from and what will justify moving it up a level.
 *
 * Proposing costs nothing. "ביצוע" realises the approved picks through the
 * calendar fill: a pack is generated only where none exists (~$0.08 each), with
 * the copy told its final date.
 *
 * Sunday morning brings a proposal by itself (three weeks ahead); "תכנון עד"
 * makes one now, to any date.
 */

const TZ = "Asia/Jerusalem";
const slotHe = (iso: string) =>
  new Date(iso).toLocaleString("he-IL", { timeZone: TZ, weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const dayHe = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("he-IL", { day: "numeric", month: "numeric" });
const plusDays = (n: number) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

const DECISION: Record<PlannerPickView["decision"], { label: string; chip: string }> = {
  pending: { label: "ממתין", chip: "bg-neutral-100 text-neutral-700" },
  approved: { label: "מאושר", chip: "bg-emerald-100 text-emerald-900" },
  replaced: { label: "הוחלף", chip: "bg-neutral-100 text-neutral-500 line-through" },
  removed: { label: "הוסר", chip: "bg-neutral-100 text-neutral-500 line-through" },
};

export function PlannerDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const dismiss = useDismiss(onClose);
  const state = useQuery({ queryKey: ["social-planner"], queryFn: () => api.socialPlanner() });
  const [until, setUntil] = useState(plusDays(21));
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<BackfillJobView | null>(null);
  const [swapFor, setSwapFor] = useState<number | null>(null);

  const proposal = state.data?.proposal ?? null;
  const metrics = state.data?.metrics;
  const running = job?.status === "running" || state.data?.job?.status === "running";

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      void api.backfillStatus().then((r) => {
        if (r.job) setJob(r.job);
        if (r.job?.status !== "running") void state.refetch();
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [running, state]);

  const propose = useMutation({
    mutationFn: () => { setError(null); return api.proposePlan(until); },
    onSuccess: () => void state.refetch(),
    onError: (e) => setError((e as Error).message),
  });
  const decide = useMutation({
    mutationFn: (v: { id: number; decision: "approved" | "removed" | "pending" }) => api.decidePlannerPick(v.id, v.decision),
    onSuccess: () => void state.refetch(),
    onError: (e) => setError((e as Error).message),
  });
  const replace = useMutation({
    mutationFn: (v: { id: number; candidateId: string }) => api.replacePlannerPick(v.id, v.candidateId),
    onSuccess: () => { setSwapFor(null); void state.refetch(); },
    onError: (e) => setError((e as Error).message),
  });
  const realize = useMutation({
    mutationFn: () => api.realizePlan(proposal!.id),
    onSuccess: (r) => { if (r.job) setJob(r.job); void state.refetch(); },
    onError: (e) => setError((e as Error).message),
  });
  const close = useMutation({
    mutationFn: () => api.closePlan(proposal!.id),
    onSuccess: () => void state.refetch(),
  });

  const live = (proposal?.picks ?? []).filter((p) => p.decision !== "replaced");
  const weeks = [...new Set(live.map((p) => p.week_start))].sort();
  const inProposal = new Set(live.filter((p) => p.decision === "pending" || p.decision === "approved").map((p) => p.candidate_id));
  const approvedToRun = live.filter((p) => p.decision === "approved" && !p.realized_at).length;
  const pending = live.filter((p) => p.decision === "pending").length;
  const summaryOf = (w: string) => proposal?.notes?.weeks.find((x) => x.weekStart === w);

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles size={15} /> תכנון אוטומטי של היומן</h2>
          <button onClick={onClose} className="text-sm text-muted hover:text-ink">סגירה</button>
        </header>

        <div className="grow space-y-3 overflow-auto p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-xs">
            <label className="flex items-center gap-1.5">
              לתכנן עד
              <input type="date" value={until} min={plusDays(1)} onChange={(e) => setUntil(e.target.value)} className="rounded border border-line px-1.5 py-1" />
            </label>
            <button
              onClick={() => propose.mutate()}
              disabled={propose.isPending || running}
              className="flex items-center gap-1 rounded-lg bg-brand-700 px-3 py-1.5 font-medium text-white disabled:opacity-40"
            >
              {propose.isPending && <Loader2 size={13} className="animate-spin" />} הצעה חדשה
            </button>
            <span className="text-muted">
              {state.data?.perWeek ?? 4} מאמרים בשבוע · רוב הנושאים ללא קשר ללוח השנה · הצעה נוצרת גם לבד בכל יום ראשון בבוקר · יצירת הצעה לא עולה כסף
            </span>
          </div>

          {metrics && (
            <p className="text-[11px] text-muted">
              רמה {metrics.level} (הצעה בלבד) · ב־{metrics.windowDays} הימים האחרונים: {metrics.decided} החלטות, {metrics.approved} אושרו כמו שהוצעו, {metrics.replaced} הוחלפו, {metrics.removed} הוסרו
              {metrics.approvalRate !== null ? ` · שיעור אישור ${Math.round(metrics.approvalRate * 100)}%` : ""}
              {metrics.readyForNextLevel ? " · המדדים מאפשרים לשקול מעבר לרמה 2 (דורש את האישור שלך)" : ""}
            </p>
          )}

          {error && <p className="rounded bg-rose-50 px-3 py-2 text-xs text-danger">{error}</p>}
          {state.isPending && <p className="flex items-center gap-2 text-xs text-muted"><Loader2 size={13} className="animate-spin" /> טוען…</p>}
          {state.data && !proposal && <p className="text-xs text-muted">אין הצעה פתוחה. בחר תאריך ולחץ "הצעה חדשה".</p>}

          {proposal && weeks.map((w) => {
            const summary = summaryOf(w);
            return (
              <section key={w} className="rounded-lg border border-line">
                <h3 className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-1.5 text-xs font-semibold">
                  שבוע {dayHe(w)}
                  {summary?.seasonalGaps.length ? (
                    <span className="font-normal text-amber-800">פער עונתי: {summary.seasonalGaps.join(", ")} (אין מאמר חזק מספיק)</span>
                  ) : null}
                </h3>
                <ul className="divide-y divide-line">
                  {live.filter((p) => p.week_start === w).map((p) => (
                    <li key={p.id} className="px-3 py-2 text-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{slotHe(p.slot_iso)}</span>
                        {p.seasonal_key && <span className="rounded bg-violet-100 px-1.5 py-px text-[10px] text-violet-900">עונתי</span>}
                        <span className={`rounded px-1.5 py-px text-[10px] ${DECISION[p.decision].chip}`}>{p.realized_at ? "בוצע" : DECISION[p.decision].label}</span>
                        <span className="grow truncate">
                          {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.label}</a> : p.label}
                        </span>
                        <span className="text-[10px] text-muted">ציון {p.score.toFixed(2)}</span>
                        {!p.realized_at && (
                          <span className="flex gap-1">
                            {p.decision !== "approved" && (
                              <button onClick={() => decide.mutate({ id: p.id, decision: "approved" })} className="flex items-center gap-0.5 rounded border border-emerald-300 px-1.5 py-0.5 text-emerald-800 hover:bg-emerald-50"><Check size={11} /> אישור</button>
                            )}
                            {p.decision !== "removed" && (
                              <button onClick={() => decide.mutate({ id: p.id, decision: "removed" })} className="flex items-center gap-0.5 rounded border border-line px-1.5 py-0.5 hover:bg-canvas"><Trash2 size={11} /> הסרה</button>
                            )}
                            {p.decision !== "pending" && (
                              <button onClick={() => decide.mutate({ id: p.id, decision: "pending" })} className="flex items-center gap-0.5 rounded border border-line px-1.5 py-0.5 hover:bg-canvas" title="ביטול ההחלטה"><Undo2 size={11} /></button>
                            )}
                            {p.alternatives.length > 0 && (
                              <button onClick={() => setSwapFor(swapFor === p.id ? null : p.id)} className="rounded border border-line px-1.5 py-0.5 hover:bg-canvas">החלפה</button>
                            )}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted">{p.why_he}</p>
                      {p.realize_note && <p className="mt-0.5 text-[11px] text-emerald-800">{p.realize_note}</p>}
                      {swapFor === p.id && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {p.alternatives.filter((a) => !inProposal.has(a.candidateId)).map((a) => (
                            <button key={a.candidateId} onClick={() => replace.mutate({ id: p.id, candidateId: a.candidateId })} className="rounded border border-line bg-canvas px-1.5 py-0.5 text-[11px] hover:bg-white">
                              {a.label} · {a.score.toFixed(2)}
                            </button>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          {job && (
            <p className="rounded-lg bg-canvas px-3 py-2 text-xs">
              {job.status === "running" ? `מבצע ${job.done + 1} מתוך ${job.total}: ${job.current ?? ""}…` : job.status === "done" ? "הביצוע הסתיים. הפוסטים ביומן כטיוטות לאישור." : `שגיאה: ${job.error}`}
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 text-xs">
          <span className="text-muted">{pending} ממתינים · {approvedToRun} מאושרים לביצוע</span>
          <span className="grow" />
          {proposal && pending > 0 && (
            <button onClick={() => live.filter((p) => p.decision === "pending").forEach((p) => decide.mutate({ id: p.id, decision: "approved" }))} className="rounded-lg border border-emerald-300 px-3 py-2 text-emerald-800 hover:bg-emerald-50">
              אישור כל הממתינים
            </button>
          )}
          {proposal && (
            <button onClick={() => close.mutate()} disabled={running} className="rounded-lg border border-line px-3 py-2 disabled:opacity-40">סגירת ההצעה</button>
          )}
          {proposal && (
            <button
              onClick={() => realize.mutate()}
              disabled={approvedToRun === 0 || running || realize.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 font-medium text-white disabled:opacity-40"
              title="יוצר פוסטים (כ־$0.08 למאמר שאין לו עדיין חבילה) ומשבץ אותם כטיוטות"
            >
              {(running || realize.isPending) && <Loader2 size={14} className="animate-spin" />}
              ביצוע המאושרים ({approvedToRun})
            </button>
          )}
          {job?.status === "done" && <button onClick={onDone} className="rounded-lg bg-brand-700 px-3 py-2 font-medium text-white">סגירה ורענון</button>}
        </footer>
      </div>
    </div>
  );
}
